<template>
    <b-row>
        <b-col cols="12">
            <h2>{{buildingName}} - Åtgärdsplan</h2>
        </b-col>
        <b-col cols="12">
            <b-tabs lazy @activate-tab="onActivateTab"
                    v-model="activeTab">
                <b-tab>
                    <template #title>
                        Åtgärdsplan
                        <b-badge variant="info">{{positions.length}}</b-badge>
                    </template>

                    <b-row>
                        <b-col md="7" sm="12" class="my-1">
                            <b-form-group label-for="filter-input"
                                          label-align-sm="right"
                                          class="mb-0">
                                <b-input-group>
                                    <b-form-input id="filter-input"
                                                  v-model="filter"
                                                  type="search"
                                                  autocomplete="off"
                                                  placeholder="Filtrera...." />
                                    <b-input-group-append>
                                        <b-button :disabled="!filter" @click="filter = ''">
                                            Rensa
                                        </b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                        <b-col sm="12" md="5" class="my-1">
                            <b-form-group label="Per sida"
                                          label-for="per-page-select"
                                          label-cols-sm="6"
                                          label-align-sm="right"
                                          class="mb-0">
                                <PaginationPageSizeSelect id="per-page-select"
                                                          v-model="perPage"
                                                          view="listBuildingPositionLacks" />
                            </b-form-group>
                        </b-col>
                        <b-col cols="12"
                               class="my-1 text-center">
                            <div class="d-flex justify-content-between align-items-center">
                                <div class="my-0 mx-auto">
                                    <b-pagination v-if="!isLoading && totalRows > perPage"
                                                  v-model="currentPage"
                                                  :total-rows="totalRows"
                                                  :per-page="perPage"
                                                  align="center" />
                                </div>
                                <div>
                                    <b-button variant="primary"
                                              :disabled="positions.length === 0"
                                              @click="onOpenBulkLackMail"
                                              class="text-right">
                                        Skicka brist(er)
                                    </b-button>
                                </div>
                            </div>
                        </b-col>
                    </b-row>

                    <SendMultipleLacksModal ref="sendMultipleLacksModal"
                                            :positions="positions"
                                            title="Välj brist(er) att skicka"
                                            @sent="getData">
                    </SendMultipleLacksModal>

                    <!--TABLE-->
                    <b-table :items="positions"
                             :fields="fields"
                             :sort-by.sync="sortBy"
                             :sort-desc.sync="sortDesc"
                             :sort-direction.sync="sortDirection"
                             :current-page="currentPage"
                             :per-page="perPage"
                             :filter="filter"
                             :filter-included-fields="['concatenatedNumbers', 'articleName', 'lackDescription', 'fixedStatusDescription']"
                             @filtered="onFiltered"
                             @context-changed="onContextChanged"
                             stacked="lg"
                             show-empty
                             small
                             :busy="isLoading">
                        <template #cell(status)="row">
                            <div class="overflow-hidden">
                                <span class="d-none d-xs-inline-block">Status: </span>
                                <ControlPositionStatusBall :status-id="row.item.statusId" />
                            </div>
                        </template>
                        <template #cell(concatenatedNumbers)="row">
                            <div class="overflow-hidden">
                                {{row.item.controlSerialNumber}}-{{ row.item.concatenatedNumbers }}
                            </div>
                        </template>
                        <template #cell(articleName)="row">
                            <div class="overflow-hidden">
                                <img v-if="row.item.fileSymbolId"
                                     style="height: 1.5rem"
                                     :src="getUrl(row.item.articleId)" />
                                {{ row.item.articleName }}
                            </div>
                        </template>
                        <template #cell(lackDescription)="row">
                            <div class="overflow-hidden">
                                {{ row.item.lackDescription }}
                            </div>
                        </template>
                        <template #cell(fixedStatusDescription)="row">
                            <div class="overflow-hidden">
                                {{ row.item.fixedStatusDescription }}
                            </div>
                        </template>
                        <template #cell(lackDueDate)="row">
                            <div class="overflow-hidden d-flex align-items-center">
                                <span>{{ row.item.lackDueDate | datetime('yyyy-MM-dd') }}</span>

                                <a href="#"
                                   class="ml-2"
                                   :id="`pm-icon-${row.item.id}-${tooltipKey}`"
                                   @click.prevent="openRescheduleModal(row.item)">
                                    <b-badge variant="blue"
                                             class="pointer px-2 py-1 text-white">
                                        P
                                    </b-badge>
                                </a>

                                <b-tooltip :key="`pm-tooltip-${row.item.id}-${tooltipKey}`"
                                           :target="`pm-icon-${row.item.id}-${tooltipKey}`"
                                           custom-class="pm-tooltip-dark"
                                           triggers="hover">
                                    <span v-if="!row.item.lackReminderDate && !row.item.lackDueDate">Sätt påminnelse</span>
                                    <span v-else>Påminnelse startar {{ (row.item.lackReminderDate || row.item.lackDueDate) | datetime('yyyy-MM-dd') }}. Tryck för att flytta fram.</span>
                                </b-tooltip>
                            </div>
                        </template>

                        <template #cell(controlDoneDate)="row">
                            <div class="overflow-hidden">
                                {{ row.item.controlDoneDate | datetime('yyyy-MM-dd') }}
                            </div>
                        </template>
                        <template #cell(actions)="row">
                            <div class="d-none d-sm-inline-block">

                                <SendLackMailModal title="Skicka brist(er)"
                                                   :lackIds="[row.item.id]">
                                    <template v-slot:button="{openSendModal}">
                                        <a href="#"
                                           style="margin-right:5px;"
                                           @click="openSendModal"
                                           title="Skicka mail">
                                            <font-awesome-icon class="text-secondary"
                                                               icon="envelope"
                                                               size="lg" />
                                        </a>
                                    </template>
                                </SendLackMailModal>

                                <a href="#"
                                   @click="onOpenSidebar(row.item)">
                                    <font-awesome-icon class="text-secondary"
                                                       icon="pen-square"
                                                       size="lg" />
                                </a>
                            </div>
                            <b-button class="text-center d-block d-sm-none"
                                      block
                                      @click="onOpenSidebar(row.item)">
                                Välj
                            </b-button>
                            <SendLackMailModal title="Skicka brist(er)"
                                               class="w-100 text-center d-block d-sm-none"
                                               :lackIds="[row.item.id]">
                                <template v-slot:button="{openSendModal}">
                                    <b-button block
                                              @click="openSendModal"
                                              style="margin-top:3px;">
                                        <font-awesome-icon class="text-white"
                                                           icon="envelope" /> Skicka mail
                                    </b-button>
                                </template>
                            </SendLackMailModal>
                        </template>
                        <template #empty="">
                            <h4 class="text-center">Det finns inga brister</h4>
                        </template>
                        <template #emptyfiltered="">
                            <div class="text-center">
                                <h4>Inga resultat</h4>
                                <h6>
                                    <a href="#" @click="filter = ''">
                                        Klicka här för att rensa aktiv filtrering
                                    </a>
                                </h6>
                            </div>
                        </template>
                    </b-table>

                    <b-row>
                        <b-col v-show="totalRows > perPage"
                               cols="12"
                               class="my-1 text-center">
                            <b-pagination v-model="currentPage"
                                          :total-rows="totalRows"
                                          :per-page="perPage"
                                          align="center"
                                          class="my-0" />
                        </b-col>
                    </b-row>
                    <b-modal id="reschedule-lack-modal"
                             v-model="showRescheduleModal"
                             title="Påminnelse-datum"
                             @ok="onRescheduleSave"
                             ok-title="Flytta fram"
                             cancel-title="Avbryt"
                             cancel-variant="warning">
                        <b-form-group label="Datum">
                            <b-form-input type="date"
                                          v-model="newReminderDate">
                            </b-form-input>
                        </b-form-group>
                    </b-modal>
                    <!--Edit-->
                    <b-sidebar v-if="sidebar.selected"
                               backdrop-variant="dark"
                               backdrop
                               right
                               lazy
                               shadow
                               :visible="sidebar.visible"
                               @hidden="onSidebarClosed">
                        <template v-slot:title>
                            <slot name="header">
                                <h4 class="text-center">
                                    Brist  {{ sidebar.selected.concatenatedNumbers }}
                                </h4>
                            </slot>
                        </template>
                        <SaveBuildingPositionLacks v-on:positionUpdated="onSidebarClosed"
                                                   :position="sidebar.selected" />
                    </b-sidebar>
                </b-tab>
                <b-tab>
                    <template #title>
                        Historik
                        <b-badge variant="info"
                                 v-if="numberOfHistoryItems !== null">
                            {{numberOfHistoryItems}}
                        </b-badge>
                    </template>
                    <ListBuildingPositionLacksHistory :buildingId="buildingId"
                                                      v-bind:numberOfHistoryItems="numberOfHistoryItems"
                                                      v-on:update:numberOfHistoryItems="numberOfHistoryItems = $event" />
                </b-tab>
            </b-tabs>
        </b-col>
    </b-row>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import { get, post } from '@/helpers/api';
    import {
        getDownloadUrlByArticleId
    } from '@/helpers/file.helper';
    import PaginationPageSizeSelect from '@/components/PaginationPageSizeSelect';
    import GenericSidebar from '@/components/GenericSidebar';
    import SaveBuildingPositionLacks from '@/components/building/SaveBuildingPositionLacks';
    import ControlPositionStatusBall from '@/components/control/ControlPositionStatusBall';
    import SendLackMailModal from '@/components/control/SendLackMailModal';
    import ListBuildingPositionLacksHistory from '@/components/building//ListBuildingPositionLacksHistory';
    import {
        getByKey as getFiltersForView,
        save as setFiltersForView
    } from '@/services/view-filter.service';
    import SendMultipleLacksModal from '@/components/control/SendMultipleLacksModal';

    export default {
        name: 'ListBuildingPositionLacks',
        components: {
            PaginationPageSizeSelect,
            GenericSidebar,
            SaveBuildingPositionLacks,
            ControlPositionStatusBall,
            SendLackMailModal,
            ListBuildingPositionLacksHistory,
            SendMultipleLacksModal
        },
        props: {
            buildingId: {
                type: Number,
                required: true
            },
            buildingName: {
                type: String,
                required: true
            }
        },
        data() {
            return {
                sidebar: {
                    visible: false,
                    selected: null
                },
                fields: [
                    {
                        key: 'status',
                        label: '',
                        thStyle: { 'width': '50px' }
                    },
                    {
                        key: 'concatenatedNumbers',
                        label: '#',
                        sortable: true,
                        sortDirection: 'desc',
                        thStyle: { 'width': '100px' }
                    },
                    {
                        key: 'articleName',
                        label: 'Artikel',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    {
                        key: 'lackDescription',
                        label: 'Brist',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    {
                        key: 'fixedStatusDescription',
                        label: 'Åtgärdstatus',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    {
                        key: 'lackDueDate',
                        label: 'Åtgärdas senast',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    {
                        key: 'controlDoneDate',
                        label: 'Rapporterad',
                        sortable: true,
                        sortDirection: 'desc',
                        tdClass: 'text-right',
                        thClass: 'text-right',
                        thStyle: { 'width': '120px' }
                    },
                    {
                        key: 'actions',
                        label: '',
                        tdClass: ['table-list--actions--wrapper', 'text-right'],
                        thClass: 'text-right',
                        thStyle: { 'width': '110px' }
                    }
                ],
                totalRows: 1,
                currentPage: 1,
                perPage: 5,
                sortBy: '',
                sortDesc: false,
                sortDirection: 'asc',
                filter: null,
                isLoading: true,
                positions: [],
                numberOfHistoryItems: null,
                activeTab: 0,
                showRescheduleModal: false,
                newReminderDate: null,
                selectedPositionId: null,
                tooltipKey: 0,
            };
        },
        computed: {
            ...mapGetters('user', ['isInRole']),
            ...mapState({
                user: (state) => state.user.user
            }),
            apiAction() {
                return `GetLacksByBuildingId/${this.buildingId}`;
            }
        },
        async mounted() {
            await this.getData();

            const viewFilters = getFiltersForView('listBuildingPositionLacksHistory');
            if (viewFilters) {
                this.setFilters(viewFilters);
            }

            const entityIdParam = this.$route.params.entityId;
            const openRescheduleModal = this.$route.query.openRescheduleModal === 'true';

            if (entityIdParam) {
                const entityId = parseInt(entityIdParam);
                const item = this.positions.find(x => x.id === entityId);

                if (item) {
                    this.onOpenSidebar(item);

                    if (openRescheduleModal) {
                        this.$nextTick(() => {
                            this.$root.$emit('lack:open-reminder-modal', entityId);
                        });
                    }
                }
            }
        },
        methods: {
            setFilters(viewFilters) {
                this.sortBy = viewFilters.sortBy;
                this.sortDesc = viewFilters.sortDesc;
                this.sortDirection = viewFilters.sortDirection;

                this.filter = viewFilters.filter;

                this.currentPage = viewFilters.currentPage;
                if (viewFilters.perPage) this.perPage = viewFilters.perPage;

                this.activeTab = viewFilters.activeTab;
            },
            onContextChanged(ctx) {
                if (!this.isLoading && ctx)
                    setFiltersForView(
                        'listBuildingPositionLacks',
                        { ...ctx, activeTab: this.activeTab }
                    );

                if (ctx.perPage > this.totalRows)
                    this.currentPage = 1;
            },
            onActivateTab(newTabIndex, prevTabIndex, bvEvent) {
                if (newTabIndex === 0)
                    this.numberOfHistoryItems = null;

                this.activeTab = newTabIndex;

                setFiltersForView(
                    'listBuildingPositionLacks',
                    {
                        sortBy: this.sortBy,
                        sortDesc: this.sortDesc,
                        sortDirection: this.sortDirection,

                        filter: this.filter,

                        currentPage: this.currentPage,
                        perPage: this.perPage,

                        activeTab: this.activeTab
                    }
                );
            },
            isSuperAdmin() {
                return this.isInRole('SuperAdmin');
            },
            onFiltered(filteredItems) {
                // Trigger pagination to update the number of buttons/pages due to filtering
                this.totalRows = filteredItems.length;
            },
            async getData() {
                await get('Control', this.apiAction)
                    .then((x) => {
                        this.positions = x.data;
                        this.totalRows = this.positions.length;
                        this.isLoading = false;
                    })
                    .catch((x) => {
                        this.isLoading = false;
                    });
            },
            getUrl(id) {
                return getDownloadUrlByArticleId(id);
            },
            onOpenSidebar(item) {
                this.sidebar.visible = true;
                this.sidebar.selected = item;
            },
            onSidebarClosed(savedItem) {
                if (savedItem) {
                    // Removes if above statusid 15. Else replaces.
                    const index = this.positions.findIndex(x => x.id === savedItem.id);
                    if (savedItem.statusId >= 15)
                        this.positions.splice(index, 1);
                    else {
                        const found = this.positions.find(x => x.id === savedItem.id);
                        found.fixedDescription = savedItem.fixedDescription;
                        found.fixedStatusDescription = savedItem.fixedStatusDescription;
                        found.statusId = savedItem.statusId;
                    }
                }

                this.sidebar = {
                    visible: false,
                    selected: null
                };
            },
            openRescheduleModal(item) {
                this.selectedPositionId = item.id;
                this.newReminderDate = this.toDateInputValue(item.lackReminderDate ?? item.lackDueDate);
                this.showRescheduleModal = true;
            },
            async onRescheduleSave(bvModalEvt) {
                bvModalEvt.preventDefault();

                if (!this.newReminderDate || !this.selectedPositionId) {
                    return;
                }

                await post('Control', `UpdateLackReminderDate/${this.selectedPositionId}`, {
                    lackReminderDate: this.newReminderDate
                })
                    .then(() => {
                        const index = this.positions.findIndex(x => x.id === this.selectedPositionId);

                        if (index !== -1) {
                            this.positions[index].lackReminderDate = this.newReminderDate;
                        }

                        this.tooltipKey += 1;

                        this.showRescheduleModal = false;
                        this.newReminderDate = null;
                        this.selectedPositionId = null;
                    })

                    .catch(() => {
                        alert('Något gick snett vid uppdatering av påminnelsedatum');
                    });
            },
            onOpenBulkLackMail() {
                if (this.$refs.sendMultipleLacksModal) {
                    this.$refs.sendMultipleLacksModal.open();
                }
            },
            toDateInputValue(value) {
                if (!value) {
                    return null;
                }

                return String(value).split('T')[0];
            },
        }
    };
</script>
<style scoped>
    .pm-tooltip-dark.bs-tooltip-auto[x-placement^=top] .tooltip-inner,
    .pm-tooltip-dark .tooltip-inner {
        background-color: #000 !important;
        color: #fff !important;
    }

    .pm-tooltip-dark .arrow::before {
        border-top-color: #000 !important;
    }

</style>